import styled, { css } from 'styled-components';

import { baseContentStyling } from './HomepageWrapper.base.styles';
import { media } from '../shared/core/styles';
import { BaseStyledModifier } from '../unmanaged/styles/BaseStyledModifiers';
import { HeadingStyling } from '../unmanaged/styles/BaseStyledStyling';

/**
 * G-Star brand values layered on the shared structure. Reused by Outlet and
 * Employeeshop (see the wrapper map in `HomepageWrapper.tsx`). Defines padding,
 * row sizing (--row-width / --row-height) and banner typography. Row-height
 * pixels and aspect ratios are layout constants, kept as named CSS variables;
 * paddings that map to the design scale use `theme.padding`.
 *
 * `theme` is destructured once for the whole block (single interpolation) so
 * brand values and `media(theme)` queries read inline.
 */
export const Wrapper = styled.div`
  ${baseContentStyling}

  ${({ theme }) => css`
    :where([class*='row--height'], .row[data-height]) {
      --contentBlock-body-padding: ${theme.padding.sm};

      gap: 2px;
      margin-block-end: 2px;

      @media ${media(theme).greaterThan('sm')} {
        --contentBlock-body-padding: ${theme.padding.md};
        --row-width: 1024;

        &:where(.row--height500, .row[data-height='XL']) {
          --row-height: 512;
        }

        &:where(.row--height400, .row[data-height='L']) {
          --row-height: 690;
        }

        &:where(.row--height300, .row--height200, .row[data-height='M'], .row[data-height='S']) {
          --row-height: 513;
        }

        &:where(.row--height150, .row[data-height='PLP']) {
          --row-height: 340;
        }

        &:where(.row--height100, .row[data-height='XS']) {
          --row-height: 146;
        }
      }

      @media ${media(theme).greaterThan('lg')} {
        --row-width: 1680;

        &:where(.row--height500, .row[data-height='XL']) {
          --row-height: 840;
        }

        &:where(.row--height400, .row[data-height='L']) {
          --row-height: 754;
        }

        &:where(.row--height300, .row[data-height='M']) {
          --row-height: 561;
        }

        &:where(.row--height200, .row[data-height='S']),
        &:where(.row--height150, .row[data-height='PLP']) {
          --row-height: 418;
        }

        &:where(.row--height100, .row[data-height='XS']) {
          --row-height: 180;
        }
      }

      @media ${media(theme).greaterThan('xxl')} {
        --banner-title-font-size: clamp(32px, calc(32 * (100 / 1680) * 1vw), 48px);
      }

      .col:first-child:last-child {
        --banner-title-font-size: 40px;

        @media ${media(theme).greaterThan('xxl')} {
          --banner-title-font-size: clamp(40px, calc(40 * (100 / 1680) * 1vw), 64px);
        }
      }

      @media ${media(theme).greaterThan('desktop-lg')} {
        --contentBlock-body-padding: 48px;
      }

      @media ${media(theme).lessThan('sm')} {
        :where(:has(.lockup-container)) {
          --row-height: 540;
        }
      }
    }

    /* after the row-sizing block: source order decides the cascade (:where) */
    /* stylelint-disable-next-line no-duplicate-selectors */
    :where([class*='row--height'], .row[data-height]) {
      :where(:not(:has(.lockup))) {
        :where(.contentBlock) {
          --contentBlock-text-align: left;
          --contentBlock-justify-content: flex-start;
          --contentBlock-align-items: flex-end;
        }

        .contentBlock-body-title,
        .contentBlock-body-subTitle {
          ${HeadingStyling}
          font-size: var(--banner-title-font-size, ${theme.font.size[8]});
        }

        .contentBlock-body-subTitle {
          font-size: var(--banner-subTitle-font-size, ${theme.font.size[7]});
        }
      }
    }

    /* Promo/SALE Header */
    :where(.contentBlock:has(.lockup-container)) {
      ${BaseStyledModifier.Promo}
      --contentBlock-text-align: left;
      --contentBlock-justify-content: flex-start;
      --contentBlock-body-padding: 6.5%;

      @media ${media(theme).greaterThan('sm')} {
        --contentBlock-body-padding: ${theme.padding.md} calc(100% / 12);
      }

      @media ${media(theme).greaterThan('desktop-lg')} {
        --contentBlock-body-padding: 48px calc(100% / 12);
      }
    }

    /* FLOWBOX */
    .row--flowbox-homepage {
      display: grid;

      .col {
        grid-area: auto;
      }

      .contentBlock-body-title {
        ${HeadingStyling}
        font-size: ${theme.font.size[7]};
      }
    }
  `}
`;
